/**
 * Centralized API Configuration
 * Handles both same-origin proxy and external backend URLs
 * 
 * This ensures consistent API calls across the entire frontend,
 * preventing environment mismatch bugs and CORS issues.
 */

/**
 * Determines the backend base URL based on environment
 * @returns {string} Backend URL (empty string for proxy, full URL for external)
 */
const getBackendUrl = () => {
  // 1. Check if running on localhost - use proxy
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔧 API Config: Detected localhost, using proxy');
    }
    return '';
  }

  // 2. Check for explicit environment variable
  if (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL.trim() !== '') {
    const url = process.env.REACT_APP_BACKEND_URL.trim();
    if (process.env.NODE_ENV === 'development') {
      console.log('🔧 API Config: Using external backend:', url);
    }
    return url;
  }

  // 3. Default to same-origin (production with Nginx)
  if (process.env.NODE_ENV === 'development') {
    console.log('🔧 API Config: Using same-origin');
  }
  return '';
};

/**
 * Gets the API base URL (includes /api prefix)
 * @returns {string} Full API base URL
 */
const getApiBaseUrl = () => {
  const backendUrl = getBackendUrl();
  return backendUrl ? `${backendUrl}/api` : '/api';
};

// Export constants
export const BACKEND_URL = getBackendUrl();
export const API_BASE_URL = getApiBaseUrl();

/**
 * Builds a complete API URL for a given endpoint
 * @param {string} endpoint - API endpoint (e.g., "/questions/similar")
 * @returns {string} Complete API URL
 * 
 * @example
 * buildApiUrl('/questions') → '/api/questions' (localhost)
 * buildApiUrl('/questions') → 'https://domain.com/api/questions' (production)
 */
export const buildApiUrl = (endpoint) => {
  // Ensure endpoint starts with /
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${API_BASE_URL}${normalizedEndpoint}`;
};

/**
 * Builds a URL for non-API backend resources (e.g., uploads, static files)
 * @param {string} path - Resource path
 * @returns {string} Complete resource URL
 */
export const buildBackendUrl = (path) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${BACKEND_URL}${normalizedPath}`;
};

/**
 * Validates if a URL is properly formatted
 * @param {string} url - URL to validate
 * @returns {boolean} True if valid
 */
export const validateUrl = (url) => {
  try {
    new URL(url, window.location.origin);
    return true;
  } catch {
    return false;
  }
};

// Development logging (only in dev mode)
if (process.env.NODE_ENV === 'development') {
  console.log('📡 API Configuration:', {
    hostname: window.location.hostname,
    BACKEND_URL,
    API_BASE_URL,
    env: process.env.REACT_APP_BACKEND_URL || '(not set)'
  });
}
